import { memo } from "react";
import { NavTreeItem } from "@repo/ui";
import type { NavItemProps } from "@repo/ui";
import type { NavItemPropsWithMetadata } from "~/utils/sidebar-transforms";
import { navigationItemLabels } from "./side-bar.data";
import { useSidebarContext, type RouteContextType } from "./sidebar-context";
import { SidebarRow } from "./sidebar-row";

type Props = {
  items: readonly NavItemProps[];
  parentPath?: string;
  showOptionDD?: boolean;
  showTreeLines?: boolean;
};

const checkActiveState = (item: NavItemProps, routeContext: RouteContextType): boolean => {
  const { isActive, isActivePrefix } = routeContext;
  const activeButton = item.to ? isActive(item.to) : !!item.isActive;

  if (!item.to) return activeButton;

  const isAdmin = item.label.includes(navigationItemLabels.administration);
  const isDashboard = item.label.includes(navigationItemLabels.dashboard);

  return ((isAdmin || isDashboard) && isActivePrefix(item.to, 1)) || activeButton;
};

export const NavItemRenderer = memo(
  ({ items, parentPath = "", showOptionDD = false, showTreeLines = true }: Props) => {
    const { expandedIds, routeContext, handlers } = useSidebarContext();
    const { isActive, isActivePrefix } = routeContext;
    return (
      <>
        {items.map((item, index) => {
          const uniqueKey = `${parentPath}/${item.label}-${index}`;
          const defaultOpen =
            typeof item.defaultOpen === "function"
              ? item.defaultOpen(isActivePrefix)
              : (item.defaultOpen ?? false);

          const activeState =
            checkActiveState(item, routeContext) ||
            (defaultOpen && Array.isArray(item.children) && item.children.length > 0);

          const resolvedIcon =
            typeof item.icon === "function" ? item.icon({ isActive, isActivePrefix }) : item.icon;

          const handleClick =
            typeof item.onClick === "string" ? handlers?.[item.onClick] : item.onClick;

          const showChevron =
            typeof item.showChevron === "function"
              ? item.showChevron(isActivePrefix)
              : (item.showChevron ?? true);

          const itemWithMeta = item as NavItemPropsWithMetadata;
          const entityId = itemWithMeta._entityId;
          const entityType = itemWithMeta._entityType;
          const isExpanded = entityId ? expandedIds.has(entityId) : defaultOpen;

          return (
            <NavTreeItem key={uniqueKey} showLines={showTreeLines}>
              <SidebarRow
                item={item}
                isActive={activeState}
                resolvedIcon={resolvedIcon}
                showChevron={showChevron}
                defaultOpen={isExpanded}
                showOptionDD={showOptionDD}
                onClick={handleClick}
                entityId={entityId}
                entityType={entityType}
              >
                {item.children &&
                  (Array.isArray(item.children) ? (
                    <NavItemRenderer
                      items={item.children}
                      parentPath={uniqueKey}
                      showOptionDD={showOptionDD}
                      showTreeLines={showTreeLines}
                    />
                  ) : (
                    item.children
                  ))}
              </SidebarRow>
            </NavTreeItem>
          );
        })}
      </>
    );
  }
);

NavItemRenderer.displayName = "NavItemRenderer";
